// Angular
import { ChangeDetectionStrategy, Component, forwardRef, Injector, ViewChild } from "@angular/core";
import { NG_VALUE_ACCESSOR, ControlValueAccessor } from "@angular/forms";
import { ObjectUtils } from "@bfx/primeng/utils";
// BCDFX - Globals

import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

// Services

// Components
import { ICD_BASE_DEFAULT_HOST_RECORD, ICDBase } from "./icd-base.component";
import { BfxAutoCompleteComponent } from "app/bfx-element-library/bfx-autocomplete.component";
// Models
import { ATDLInterfaceControlModel } from "../../data-atdl/model/atdl-interface-control.model";
import { ICDAutocompleteModel } from "../../data-idc/controls/icd-autocomplete.model";
import { ICDAuxCheckboxModel } from "../../data-idc/controls/icd-aux-checkbox.model";

// Enums - ICD Coordination
import { ICDNResponseCommand } from "../../data-idc/enums/icd-response-cmd.enum";
// Enums - ATDL
import { ATDLEnumModel } from "../../data-atdl/model/atdl-enum.model";
// Enums
import { ICNInputControlState } from "../controls/models/bfx-interface-controls.enums";
import { ICDNAuxMode } from "../../data-idc/enums/icd-aux-mode.enum";
import { ICDEnumModel } from "../../data-idc/controls/icd-enum.model";

export const AUTOCOMPLETE_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => ICDAutocompleteComponent),
  multi: true,
};

@Component({
  selector: "icd-autocomplete",
  host: {
    ...ICD_BASE_DEFAULT_HOST_RECORD,
    "[class.icd__autocomplete__container]": "true",
  },
  templateUrl: "./html/icd-autocomplete.html",
  providers: [AUTOCOMPLETE_VALUE_ACCESSOR, ObjectUtils],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ICDAutocompleteComponent
  extends ICDBase<ICDAutocompleteModel>
  implements ControlValueAccessor
{
  /**********************************************************************/
  /* DATA */

  public config: any = {
    resultsToShow: 30,
  };

  public subData: any = {
    item: null, // ICDEnumModel -> used in the autocomplete interface for display (intermediate model)
    dataset: [], // complete dataset of all autocomplete options, used for local filtering
    filteredItems: [], //
    defaultValue: null,
    initialValue: null,
  };

  /************************************/
  onModelChange: Function = () => {};
  onModelTouched: Function = () => {};

  /************************************/
  /* Element Ref */
  @ViewChild("inputField", { static: true })
  inputField: BfxAutoCompleteComponent;

  /**********************************************************************/
  /* CONSTRUCTOR  + LIFE CYCLE HOOKS */

  constructor(injector: Injector) {
    super(injector);
    this.oeLogger = this.oeLogger.for(__dirname);
    this.initialize();
  }

  /**********************************************************************/
  /* INITIALIZE */

  protected initialize(): void {
    super.initialize();
    this.label = "Autocomplete";
  }

  /**********************************************************************/
  /* DYNAMIC CONTROL API  */

  protected initializeICDSpecificModelProperties(): boolean {
    this.cd.markForCheck();

    let error: boolean = false;
    if (this.model instanceof ATDLInterfaceControlModel) {
      // DATASET
      if (this.model.dataset instanceof Array && this.model.dataset.length > 0) {
        for (let dataItem of this.model.dataset) {
          if (dataItem instanceof ATDLEnumModel) {
            let uiRep = dataItem.uiRep;
            let tooltip = "";
            if (typeof uiRep === "string" && uiRep.length > 28) {
              uiRep = uiRep.substring(0, 28) + "..";
              tooltip = dataItem.uiRep;
            }
            this.data.dataset.push(
              new ICDEnumModel(dataItem.enumId, uiRep, dataItem.enumId, tooltip),
            );
          }
        }
        this.data.filteredItems = [...this.data.dataset];
      }
      // INIT VALUE
      if (typeof this.model.initValue === "string") {
        if (
          this.data.dataset.find((item) => item.value === this.model.initValue) instanceof
          ICDEnumModel
        ) {
          this.setAndUpdateValue(this.model.initValue);
          this.data.defaultValue = this.model.initValue;
        }
        // DEFAULT VALUE - this is not compliant with ATDL specification, but used in BCDF ATDLs
      } else if (typeof this.model.defaultValue === "string") {
        if (
          this.data.dataset.find((item) => item.value === this.model.defaultValue) instanceof
          ICDEnumModel
        ) {
          this.setAndUpdateValue(this.model.defaultValue);
          this.data.defaultValue = this.model.defaultValue;
        }
      }
    } else {
      error = true;
    }
    return error;
  }

  /**********************************************************************/
  /* DYNAMIC CONTROL - HANDLERS */

  protected handleConstraintUpdate(data: any): void {
    this.handleDynResponses(this.processExternalStateFlowDependency(data, new Map()));
  }

  /**********************************************************************/
  /* ControlValueAccessor */

  writeValue(value: any): void {
    this.data.value = value;
    this.model.value = this.data.value;
  }

  registerOnChange(fn: Function): void {
    this.onModelChange = fn;
  }

  registerOnTouched(fn: Function): void {
    this.onModelTouched = fn;
  }

  /**********************************************************************/
  /* EVENTS */

  public onFocus(event: any): void {
    this.inputFocused = true;
    this.dirty = true;
    this.data.state = ICNInputControlState.ACTIVE;
    this.data.errorMsg = "";
  }

  public onBlur(event: any): void {
    let dynResponses = new Map();
    this.inputFocused = false;
    this.updateValue();
    this.validate();
    this.data.defaultValue =
      this.data.item instanceof ICDEnumModel || this.data.item === "string"
        ? this.data.item
        : this.data.defaultValue;
    dynResponses.set(ICDNResponseCommand.UPDATE_VALUE, this.data.value);
    dynResponses = this.processAuxStateFlow(dynResponses);
    this.handleDynResponses(dynResponses);
  }

  public onChange(event: any): void {
    this.onModelChange(this.data.value);
  }

  public onSelect(event: any): void {}

  public focusInput() {
    this.inputFocused = true;
    this.dirty = true;
    setTimeout(() => this.inputField.focusInput(), 50);
  }

  /**********************************************************************/
  /* EXTERNAL API */

  public setDefault(): void {
    this.setAndUpdateValue(this.data.defaultValue);
  }

  protected setAndUpdateValue(value: any): void {
    this.cd.markForCheck();

    if (value == null || (value instanceof ICDAutocompleteModel && value.value == null)) {
      this.set(null);
      this.updateValue();
    } else if (value instanceof ICDEnumModel) {
      this.set(value);
      this.updateValue();
    } else if (typeof value === "string") {
      let result = this.data.dataset.find(
        (item) =>
          item.value.toLowerCase() === value.toLowerCase() ||
          item.label.toLowerCase() === value.toLowerCase(),
      );
      if (result instanceof ICDEnumModel) {
        this.set(result);
        this.updateValue();
      } else {
        logger.out.error(
          `ERROR: ${this.constructor.name}.setAndUpdateValue(): Value string but no match in dataset`,
        );
      }
    } else if (value instanceof ICDAutocompleteModel) {
      let result =
        value.value != null
          ? this.data.dataset.find((item) => item.value.toLowerCase() === value.value.toLowerCase())
          : null;
      if (result instanceof ICDAutocompleteModel) {
        this.set(value);
        this.updateValue();
      } else {
        logger.out.error(
          `ERROR: ${this.constructor.name}.setAndUpdateValue(): Value ICDAutocompleteModel but no match in dataset`,
        );
      }
    } else {
      logger.out.error(`ERROR: ${this.constructor.name}.setAndUpdateValue(): Invalid value`);
    }
  }

  public updateValue(): void {
    this.cd.markForCheck();
    this.data.value = this.compose();
    this.writeValue(this.data.value);
  }

  public load(value: ICDAutocompleteModel, propagate?: boolean): void {
    let dynResponses = new Map();

    if (value instanceof ICDAutocompleteModel || value === null) {
      this.cd.markForCheck();

      this.set(value);
      this.updateValue();
      this.validate();

      dynResponses.set(ICDNResponseCommand.LOAD_COMPLETE, null);

      dynResponses = this.processAuxStateFlow(dynResponses);

      if (typeof propagate === "boolean" && propagate) {
        dynResponses.set(ICDNResponseCommand.UPDATE_VALUE, this.data.value);
      }

      this.handleDynResponses(dynResponses);
    }
  }

  public set(value: ICDAutocompleteModel | ICDEnumModel | string): void {
    if (
      value instanceof ICDAutocompleteModel ||
      value instanceof ICDEnumModel ||
      typeof value === "string" ||
      value == null
    ) {
      this.dirty = true;
      this.cd.markForCheck();

      if (value instanceof ICDAutocompleteModel && value.auxModel instanceof ICDAuxCheckboxModel) {
        this.disabled = this.data.disabled = value.disabled;
        let enumItem =
          value.value != null
            ? this.data.dataset.find(
                (item) => item.value.toLowerCase() === value.value.toLowerCase(),
              )
            : null;
        if (value.auxModel.enabled) {
          this.auxCheckbox.enabled = value.auxModel.enabled;
          this.auxCheckbox.mode = value.auxModel.mode;

          if (value.auxModel.mode === ICDNAuxMode.DISABLED) {
            this.data.item = null;
            //
            this.auxCheckbox.value = false;
            this.auxCheckbox.locked = true;
          } else if (value.auxModel.mode === ICDNAuxMode.REQUIRED) {
            this.data.item = enumItem != null ? enumItem : null;
            //
            this.auxCheckbox.value = true;
            this.auxCheckbox.locked = true;
          } else if (value.auxModel.mode === ICDNAuxMode.OPTIONAL) {
            this.data.item = enumItem != null ? enumItem : null;
            //
            this.auxCheckbox.value = value.auxModel.value;
            this.auxCheckbox.locked = false;
          } else {
            logger.out.error(`ERROR: ${this.constructor.name}.set(): Invalid auxMode`);
          }
        } else {
          this.data.item = enumItem != null ? enumItem : null;
          //
          this.auxCheckbox.enabled = false;
          this.auxCheckbox.mode = ICDNAuxMode.DISABLED;
          this.auxCheckbox.value = false;
          this.auxCheckbox.locked = true;
        }
      } else if (value instanceof ICDAutocompleteModel && value.auxModel === null) {
        let enumItem =
          value.value != null
            ? this.data.dataset.find(
                (item) => item.value.toLowerCase() === value.value.toLowerCase(),
              )
            : null;
        this.data.item = enumItem != null ? enumItem : null;
        this.disabled = this.data.disabled = value.disabled;
      } else if (typeof value === "string") {
        let result = this.data.dataset.find(
          (item) =>
            item.value.toLowerCase() === value.toLowerCase() ||
            item.label.toLowerCase() === value.toLowerCase(),
        );
        if (result instanceof ICDEnumModel) {
          this.data.item = value;
        }
      } else if (value instanceof ICDEnumModel) {
        if (this.data.dataset.find((item) => item.value === value.value) != null) {
          this.data.item = value;
        }
      } else if (value == null) {
        this.data.item = null;
      } else {
        logger.out.error(`ERROR: ${this.constructor.name}.set(): Invalid value type`);
      }
    }
  }

  private compose(): ICDAutocompleteModel {
    let model = new ICDAutocompleteModel();
    let auxModel = new ICDAuxCheckboxModel();
    //
    let dataItem = this.data.item;
    let value = null;
    let label = null;
    if (
      dataItem instanceof ICDEnumModel &&
      this.data.dataset.find((item) => item.value.toLowerCase() === dataItem.value.toLowerCase()) !=
        null
    ) {
      value = dataItem.value;
      label = dataItem.label;
    } else if (typeof dataItem === "string") {
      let enumItem = this.data.dataset.find(
        (item) => item.value === dataItem || item.label.toLowerCase() === dataItem.toLowerCase(),
      );
      if (enumItem instanceof ICDEnumModel) {
        value = enumItem.value;
        label = enumItem.label;
      }
    }
    //
    if (this.auxCheckbox.enabled) {
      auxModel.auxId = this.auxCheckbox.id;
      auxModel.enabled = this.auxCheckbox.enabled;
      auxModel.mode = this.auxCheckbox.mode;
      auxModel.value = this.auxCheckbox.value;
      if (this.auxCheckbox.mode === ICDNAuxMode.DISABLED) {
        model.value = null;
        model.label = "";
      } else if (this.auxCheckbox.mode === ICDNAuxMode.REQUIRED) {
        model.value = value;
        model.label = label;
      } else if (this.auxCheckbox.mode === ICDNAuxMode.OPTIONAL) {
        model.value = this.auxCheckbox.value ? value : null;
        model.label = this.auxCheckbox.value ? label : "";
      } else {
        logger.out.error(`ERROR: ${this.constructor.name}.set(): Invalid auxMode`);
        model.value = value;
        model.label = label;
      }
    } else {
      auxModel.enabled = this.auxCheckbox.enabled; // false
      auxModel.auxId = this.auxCheckbox.id;
      auxModel.mode = this.auxCheckbox.mode;
      //
      model.value = value;
      model.label = label;
    }
    model.nulledByDependency = this.data?.value?.nulledByDependency ?? false;
    model.disabled = this.disabled || this.data.disabled || !this.visible;
    model.auxModel = auxModel;
    return model;
  }

  public validateIC(): boolean {
    this.cd.markForCheck();

    if (
      this.data.state != ICNInputControlState.INVALID &&
      this.auxCheckbox.enabled &&
      !this.auxCheckbox.value
    ) {
      this.data.state = ICNInputControlState.INACTIVE;
      this.data.errorMsg = "";
      return true;
    }

    let selectedItem = this.data.item;
    if (selectedItem != null && selectedItem !== "") {
      let result = this.data.dataset.find((item) => {
        if (selectedItem.label != null) {
          return item.label.toLowerCase() === selectedItem.label.toLowerCase();
        }
        return item.label.toLowerCase() === selectedItem.toLowerCase();
      });
      if (result != null) {
        this.data.state = ICNInputControlState.VALID;
        this.data.errorMsg = "";
        return true;
      } else if (this.data.state !== ICNInputControlState.INACTIVE || this.dirty) {
        this.data.state = ICNInputControlState.INVALID;
        this.data.errorMsg = "Value not in dataset!";
        return false;
      } else {
        return true;
      }
    } else {
      if (
        this.data.state !== ICNInputControlState.INACTIVE &&
        (this.required || (this.auxCheckbox.enabled && this.auxCheckbox.value))
      ) {
        this.data.state = ICNInputControlState.INVALID;
        this.data.errorMsg = `${this.label} must not be empty`;
        return false;
      } else if (this.data.state !== ICNInputControlState.INACTIVE && !this.required) {
        this.data.state = ICNInputControlState.INACTIVE;
        this.data.errorMsg = "";
        return true;
      } else {
        return true;
      }
    }
  }

  public resetIC(): void {
    this.cd.markForCheck();

    this.data.item = null;
    this.data.filteredItems = [];
    this.data.dataset = [];
    this.data.defaultValue = null;
    this.data.initialValue = null;
  }

  /**********************************************************************/
  /* AUTOCOMPLETE */

  public search(event: any): void {
    this.cd.markForCheck();

    let filteredItems = [];
    let allItems = this.data.dataset;
    for (let i = 0; i < allItems.length; i++) {
      let item = allItems[i];
      if (
        event.query.length === 0 ||
        item.label.toLowerCase().indexOf(event.query.toLowerCase()) >= 0
      ) {
        filteredItems.push(item);
      }
    }
    this.data.filteredItems = filteredItems;
  }
}
